"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { Shield, Eye, Target, Award, Rocket, CheckCircle, ArrowRight, CheckCircle2, ShieldCheck, Database, Zap, Sparkles, Network, Cpu, Users, Layers, Workflow, Check } from "lucide-react";
import { Card } from "@/components/ui/Card";

export default function AboutContent() {
  const values = [
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Innovation",
      description: "We continually research next-gen cloud containers and LLM features to bring optimization to your business operations.",
    },
    {
      icon: <Shield className="h-6 w-6 text-accent" />,
      title: "Transparency",
      description: "We share full git repository access, compile weekly working demos, and provide flat pricing sheets to remove surprises.",
    },
    {
      icon: <Award className="h-6 w-6 text-primary" />,
      title: "Quality",
      description: "We run unit test coverage, cross-browser audits, accessibility validations, and performance caching to build stable code.",
    },
  ];

  const choosingFactors = [
    "Dedicated project manager assigned to every build.",
    "Full IP ownership and private Git repository transfer.",
    "OWASP-compliant TLS 1.3 security setups.",
    "Weekly interactive product walkthroughs.",
    "Post-deployment maintenance and system upgrades.",
  ];

  return (
    <div className="w-full bg-[#F8F8F6] text-[#111318] overflow-x-hidden">
      {/* Architectural Hero Section */}
      <section className="relative min-h-[70vh] w-full flex items-center justify-start px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900">
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/reception.webp"
            alt="Johnson Softwares Corporate Reception"
            fill
            priority
            sizes="100vw"
            quality={85}
            className="object-cover object-center"
          />
        </div>

        {/* Localized subtle left gradient behind text */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[65%] lg:w-[50%] bg-gradient-to-r from-white/95 via-white/80 to-transparent pointer-events-none z-[1]" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-2xl text-left">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-xs text-xs font-mono text-[#111318] mb-6">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
            <span className="tracking-wide font-medium">COMPANY PROFILE // PRINCIPAL ARCHITECTURE &amp; DELIVERY</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-[#111318] tracking-tight leading-[1.08] mb-6">
            WE BUILD SYSTEMS AROUND
            <br />
            <span className="text-[#2563EB]">HOW YOU ACTUALLY WORK.</span>
          </h1>

          <p className="text-base sm:text-lg text-[#374151] leading-relaxed mb-8 max-w-xl font-normal">
            We operate with a core architecture and delivery team, supported by a vetted engineering network that we scale based on project requirements. From custom sales pipelines to bespoke enterprise software, we build systems tailored to your exact operational reality.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 mb-8">
            <Link
              href="/contact"
              className="px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] flex items-center justify-center gap-2 group"
            >
              <span>CONNECT WITH ARCHITECTS</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              href="/case-studies"
              className="px-8 py-3.5 rounded-xl bg-white/95 hover:bg-white border border-[#111318]/15 hover:border-[#2563EB] text-[#111318] hover:text-[#2563EB] font-semibold text-sm transition-all text-center shadow-xs"
            >
              VIEW CLIENT EVIDENCE
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-[#374151] pt-4 border-t border-[#111318]/10">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>Direct Principal Contact</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>100% IP &amp; Code Transfer</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5 text-[#2563EB]" />
              <span>Core Team + Scaled Network</span>
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 sm:px-8 lg:px-12 py-20">
        {/* Company Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <div className="p-8 sm:p-12 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#2563EB] block mb-2 font-bold">
              FOUNDATIONAL THESIS
            </span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#111318] mb-4 tracking-tight">
              Software Should Multiply Operational Capacity, Not Tax It.
            </h2>
            <p className="text-base text-[#5E636B] leading-relaxed mb-4">
              Founded to bridge the gap between complex software engineering and pragmatic business growth, Johnson Softwares replaces disconnected spreadsheets and predatory per-seat SaaS tools with owned, autonomous software systems.
            </p>
            <p className="text-base text-[#5E636B] leading-relaxed">
              We engineer with modern TypeScript, React, Next.js, and dedicated PostgreSQL / SQLite databases. By enforcing clean domain logic and zero proprietary lock-ins, our clients retain complete freedom and unconstrained operational velocity.
            </p>
          </div>
        </motion.div>

        {/* Core Architecture + Vetted Delivery Network Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <div className="p-8 sm:p-12 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="max-w-3xl mb-8">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#2563EB] font-bold block mb-2">
                DELIVERY ARCHITECTURE // THE NETWORK MODEL
              </span>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#111318] mb-4 tracking-tight">
                Core Architectural Rigor. Scalable Engineering Power.
              </h2>
              <p className="text-base text-[#5E636B] leading-relaxed mb-4">
                Traditional software agencies pass on massive bench overhead and junior delegation. Solo contractors lack institutional stability. We operate on a modern, disciplined engineering structure:
              </p>
              <div className="p-4 rounded-xl bg-[#2563EB]/5 border-l-4 border-[#2563EB] text-sm text-[#111318] font-medium">
                &ldquo;We operate with a core architecture and delivery team, supported by a vetted engineering network that we scale based on project requirements.&rdquo;
              </div>
            </div>

            {/* Grid: Core Team vs Vetted Network */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
              {/* Core Team Card */}
              <div className="lg:col-span-6 p-6 sm:p-8 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-lg bg-[#2563EB] text-white">
                      <Cpu className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-heading font-extrabold text-lg text-[#111318]">
                        Johnson Softwares — Core Team
                      </h3>
                      <p className="text-xs text-[#5E636B] font-mono">
                        Direct Governance &amp; Principal Accountability
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-[#5E636B] mb-5 leading-relaxed">
                    You work directly with principal engineering leaders who own the system architecture, code quality, and commercial outcomes from day one.
                  </p>
                  <div className="space-y-2.5">
                    {[
                      { role: "Solution Architecture", desc: "System topology, schema modeling & domain logic" },
                      { role: "Technical Direction & Code Review", desc: "Every commit audited for security, OWASP & speed" },
                      { role: "Client Relationship & Strategy", desc: "Direct principal access with zero account executive noise" },
                      { role: "Product & Project Management", desc: "Weekly working demos, clear milestones & strict scope control" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white border border-[#111318]/5">
                        <CheckCircle2 className="w-4 h-4 text-[#2563EB] shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs font-bold text-[#111318]">{item.role}</div>
                          <div className="text-[11px] text-[#5E636B]">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vetted Network Card */}
              <div className="lg:col-span-6 p-6 sm:p-8 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-lg bg-[#111318] text-white">
                      <Network className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-heading font-extrabold text-lg text-[#111318]">
                        On-Demand Engineering Capacity
                      </h3>
                      <p className="text-xs text-[#5E636B] font-mono">
                        Vetted Specialists Scaled To Your Exact Stack
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-[#5E636B] mb-5 leading-relaxed">
                    We maintain an actively vetted roster of specialized engineers assembled on-demand to match your exact technology stack and delivery timeline.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[
                      { domain: "React & Web Frontend", tag: "Next.js, Tailwind, WebSockets" },
                      { domain: "Backend & Systems", tag: "Node.js, PostgreSQL, Redis" },
                      { domain: "Mobile Engineering", tag: "React Native, Android & iOS" },
                      { domain: "UI/UX & Design Systems", tag: "Figma, Token Architecture" },
                      { domain: "QA & Test Automation", tag: "Playwright, Jest, CI/CD testing" },
                      { domain: "DevOps & Cloud", tag: "Docker, AWS, Hetzner, Linux VPS" },
                    ].map((item, i) => (
                      <div key={i} className="p-3 rounded-lg bg-white border border-[#111318]/5 flex flex-col justify-between">
                        <div className="text-xs font-bold text-[#111318] mb-1">{item.domain}</div>
                        <div className="text-[10px] font-mono text-[#2563EB]">{item.tag}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Why This Matters for Clients Banner */}
            <div className="p-6 rounded-xl bg-gradient-to-r from-[#111318] to-slate-900 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-1 max-w-xl">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#2563EB] font-bold">
                  COMMERCIAL ADVANTAGE
                </span>
                <h4 className="text-base sm:text-lg font-bold">
                  Zero Bench Markup. 100% Focused Engineering.
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  You never pay for an agency&apos;s idle employees. You get senior architectural leadership directing dedicated specialists chosen specifically for your project.
                </p>
              </div>
              <Link
                href="/services"
                className="shrink-0 px-6 py-3 rounded-lg bg-[#2563EB] hover:bg-blue-600 text-white text-xs font-bold tracking-wide transition-all shadow-md flex items-center gap-2"
              >
                <span>EXPLORE SERVICES &amp; MODELS</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="h-full"
          >
            <div className="h-full p-8 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col justify-between">
              <div>
                <div className="p-3 bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-xl text-[#2563EB] w-fit mb-6">
                  <Rocket className="h-6 w-6" />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#5E636B] block mb-2 font-bold">
                  OUR MISSION
                </span>
                <h3 className="font-heading font-extrabold text-2xl text-[#111318] mb-3 tracking-tight">
                  High-Velocity Business Autonomy
                </h3>
                <p className="text-sm text-[#5E636B] leading-relaxed">
                  To liberate growing enterprises from manual data re-entry, brittle integrations, and per-user SaaS taxes by engineering bespoke digital engines they permanently own.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="h-full"
          >
            <div className="h-full p-8 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col justify-between">
              <div>
                <div className="p-3 bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-xl text-[#2563EB] w-fit mb-6">
                  <Eye className="h-6 w-6" />
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#5E636B] block mb-2 font-bold">
                  OUR VISION
                </span>
                <h3 className="font-heading font-extrabold text-2xl text-[#111318] mb-3 tracking-tight">
                  The Gold Standard in Systems Engineering
                </h3>
                <p className="text-sm text-[#5E636B] leading-relaxed">
                  To be recognized as India's premier bespoke software development firm, respected for architectural resilience, transparent client partnerships, and measurable enterprise value.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase block mb-2 font-semibold">
              ENGINEERING INTEGRITY
            </span>
            <h2 className="font-heading font-extrabold text-3xl text-[#111318] tracking-tight">
              Our Non-Negotiable Principles
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((val, idx) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
              >
                <div className="h-full p-8 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <div className="mb-5 p-2.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 w-fit">
                    {val.icon}
                  </div>
                  <h3 className="font-heading font-extrabold text-xl text-[#111318] mb-2">
                    {val.title}
                  </h3>
                  <p className="text-xs text-[#5E636B] leading-relaxed">
                    {val.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-16"
        >
          <div className="p-8 sm:p-12 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="flex items-center justify-between pb-4 border-b border-[#111318]/10 mb-8">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#2563EB] font-bold block mb-1">
                  CLIENT PARTNERSHIP ADVANTAGES
                </span>
                <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#111318] tracking-tight">
                  Why Fast-Moving Teams Build With Us
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {choosingFactors.map((factor) => (
                <div key={factor} className="flex items-start gap-3 p-3.5 rounded-xl bg-[#F8F8F6] border border-[#111318]/5 text-xs text-[#111318] font-semibold">
                  <CheckCircle2 className="h-4 w-4 text-[#2563EB] shrink-0 mt-0.5" />
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Boardroom Consultation Prompt */}
        <div className="p-8 sm:p-12 rounded-2xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center max-w-4xl mx-auto">
          <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase block mb-2 font-semibold">
            START YOUR CONVERSATION
          </span>
          <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#111318] mb-3 tracking-tight">
            Ready to engineer systems built to scale?
          </h3>
          <p className="text-sm text-[#5E636B] mb-8 max-w-xl mx-auto leading-relaxed">
            Speak directly with our engineering leadership to plan your product roadmap and evaluate development timelines.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5">
            <Link
              href="/contact"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)]"
            >
              Book Direct Consultation
            </Link>
            <a
              href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I'd%20like%20to%20learn%20more%20about%20your%20company%20and%20services."
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#F8F8F6] hover:bg-white border border-[#111318]/15 text-[#111318] font-semibold text-sm transition-all"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
